/**
 * Salidas a producción (demo localStorage).
 * Un registro por cíclico + día: cantidades por celda + estado (presupuestado | salida a producción).
 */

import { getMockIngredientesReceta, getMockRecetasListado } from './mockRecetasClassification';
import { loadCiclosCiclicos, etiquetaSemana } from './mockCiclosCiclicos';
import { loadPlantillasCiclicos } from './mockPlantillasCiclicos';
import {
  RECETA_QTY_DECIMAL_FACTOR,
  parseRecetaOutputQuantity,
  recetaScaleFactor,
} from '../utils/recipeQuantityLimits';

export const STORAGE_SALIDAS_PRODUCCION = 'pc_salidas_produccion_v2';
const STORAGE_LEGACY_V1 = 'pc_salidas_produccion_v1';

export const ESTADO_SALIDA = {
  PRESUPUESTADO: 'presupuestado',
  SALIDA_PRODUCCION: 'salida_produccion',
};

export const ESTADO_LABEL = {
  [ESTADO_SALIDA.PRESUPUESTADO]: 'Presupuestado',
  [ESTADO_SALIDA.SALIDA_PRODUCCION]: 'Salida a producción',
};

export const DIAS_KEYS_ORDEN = [
  'lunes',
  'martes',
  'miercoles',
  'jueves',
  'viernes',
  'sabado',
  'domingo',
];

export const DIAS_LABEL = {
  lunes: 'Lunes',
  martes: 'Martes',
  miercoles: 'Miércoles',
  jueves: 'Jueves',
  viernes: 'Viernes',
  sabado: 'Sábado',
  domingo: 'Domingo',
};

export function esDiaKeyValido(key) {
  return DIAS_KEYS_ORDEN.includes(String(key || ''));
}

/** Igual que en CiclicoDetalle: varios platos unidos con " + "). */
export function parseLineasCelda(valor) {
  if (valor == null || String(valor).trim() === '') return [];
  const s = String(valor).trim();
  if (s === 'XX') return [];
  const parts = s
    .split(/\s*\+\s*/)
    .map((x) => x.trim())
    .filter((x) => x.length > 0 && x !== '—');
  return parts.length ? parts : [];
}

/** Fecha ISO (yyyy-mm-dd) por día de la semana a partir del lunes del cíclico. */
export function fechasPorDiaDesdeInicio(fechaInicioYmd) {
  const map = {};
  try {
    const d0 = new Date(`${fechaInicioYmd}T12:00:00`);
    if (Number.isNaN(d0.getTime())) return map;
    DIAS_KEYS_ORDEN.forEach((k, i) => {
      const x = new Date(d0);
      x.setDate(d0.getDate() + i);
      const y = x.getFullYear();
      const m = String(x.getMonth() + 1).padStart(2, '0');
      const dd = String(x.getDate()).padStart(2, '0');
      map[k] = `${y}-${m}-${dd}`;
    });
  } catch {
    /* ignore */
  }
  return map;
}

export function formatearDiaCorto(ymd) {
  try {
    const d = new Date(`${ymd}T12:00:00`);
    if (Number.isNaN(d.getTime())) return ymd;
    return d.toLocaleDateString('es-ES', { weekday: 'short', day: 'numeric', month: 'short' });
  } catch {
    return ymd;
  }
}

/** Precio unitario demo por nombre de producto (hasta API de costos). */
export function precioUnitarioDemo(productName) {
  const s = String(productName || 'x');
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return Math.round((2 + (h % 1800) / 100) * 100) / 100;
}

/**
 * Costo de una receta (base 1 menú) según ingredientes mock × precios demo.
 */
export function costoBaseRecetaPorCodigo(codigo) {
  const ing = getMockIngredientesReceta(codigo);
  let total = 0;
  for (const row of ing) {
    const q = parseFloat(String(row.quantity).replace(',', '.')) || 0;
    total += q * precioUnitarioDemo(row.product);
  }
  return Math.round(total * 100) / 100;
}

/**
 * Compara nombres de platos (menú) con el catálogo de recetas elaboradas.
 */
export function normalizarNombrePlatoBusqueda(s) {
  return String(s || '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
    .replace(/\s+/g, ' ');
}

/**
 * @param {string} nombrePlato Texto de una línea del menú (un plato).
 * @param {Array<{ id?: unknown, name?: string }>} recetas Catálogo desde API (p. ej. `fetchDatosPlaneamientoCiclicos` → recetas).
 * @returns {object | null}
 */
export function buscarRecetaPlatoEnCatalogo(nombrePlato, recetas) {
  const t = normalizarNombrePlatoBusqueda(nombrePlato);
  if (!t || t === 'xx') return null;
  const list = Array.isArray(recetas) ? recetas : [];
  let exact = null;
  let best = null;
  let bestLen = -1;
  for (const r of list) {
    const rn = normalizarNombrePlatoBusqueda(r?.name);
    if (!rn) continue;
    if (rn === t) {
      exact = r;
      break;
    }
    if (t.includes(rn) || rn.includes(t)) {
      if (rn.length > bestLen) {
        bestLen = rn.length;
        best = r;
      }
    }
  }
  if (exact) return exact;
  return best;
}

/**
 * Resolución legacy (mock de clasificación). Sin coincidencia devuelve null — nunca asigna una receta al azar.
 * @param {string} nombrePlato
 * @returns {string|null} código tipo R-00n
 */
export function resolverCodigoRecetaPorNombrePlato(nombrePlato) {
  const t = String(nombrePlato || '').trim();
  if (!t || t === '—' || t === 'XX') return null;
  const list = getMockRecetasListado();
  const low = t.toLowerCase();
  const exact = list.find((r) => r.name.toLowerCase() === low);
  if (exact) return exact.code;
  const partial = list.find(
    (r) => low.includes(r.name.toLowerCase()) || r.name.toLowerCase().includes(low),
  );
  if (partial) return partial.code;
  return null;
}

export function ingredientesEscalados(codigo, factorPorciones) {
  const f = Math.max(0, Number(factorPorciones) || 0);
  const ing = getMockIngredientesReceta(codigo);
  return ing.map((row) => {
    const qBase = parseFloat(String(row.quantity).replace(',', '.')) || 0;
    const q = Math.round(qBase * f * 10000) / 10000;
    const unit = precioUnitarioDemo(row.product);
    const sub = Math.round(q * unit * 100) / 100;
    return {
      product: row.product,
      unit: row.unit,
      quantity: q,
      precioUnitario: unit,
      subtotal: sub,
    };
  });
}

/** Precio ref. por ítem de receta (API). Si no hay dato, 0. */
export function precioUnitarioInsumoDesdeItem(item) {
  if (!item || typeof item !== 'object') return 0;
  const raw =
    item.unit_price ??
    item.unitPrice ??
    item.product?.average_cost ??
    item.product?.reference_price ??
    item.product?.last_purchase_price ??
    0;
  const n = parseFloat(String(raw).replace(',', '.'));
  return Number.isFinite(n) && n >= 0 ? n : 0;
}

/** Merma planificada % sobre cantidad neta (0 si no aplica). */
export function parseRecetaMermaPct(item) {
  if (!item || typeof item !== 'object') return 0;
  const n = parseFloat(String(item.merma_pct ?? 0).replace(',', '.'));
  return Number.isFinite(n) && n > 0 ? n : 0;
}

/** Bruto = neto × (1 + merma%/100). */
export function cantidadBrutaConMerma(neto, mermaPct) {
  const n = Number(neto) || 0;
  const m = Number(mermaPct) || 0;
  if (n <= 0) return 0;
  if (m <= 0) return Math.round(n * RECETA_QTY_DECIMAL_FACTOR) / RECETA_QTY_DECIMAL_FACTOR;
  return Math.round(n * (1 + m / 100) * RECETA_QTY_DECIMAL_FACTOR) / RECETA_QTY_DECIMAL_FACTOR;
}

/** Costo base 1 porción según `items` de receta plato (API). Usa cantidad bruta (con merma). */
export function costoBaseRecetaPlatoApi(receta) {
  if (!receta || !Array.isArray(receta.items) || receta.items.length === 0) return 0;
  let total = 0;
  for (const it of receta.items) {
    const qNet = parseFloat(String(it.quantity ?? 0).replace(',', '.')) || 0;
    const q = cantidadBrutaConMerma(qNet, parseRecetaMermaPct(it));
    total += q * precioUnitarioInsumoDesdeItem(it);
  }
  return Math.round(total * 100) / 100;
}

export function lineasInsumoEscaladosRecetaApi(receta, cantidadElaborada) {
  const f = recetaScaleFactor(receta, cantidadElaborada);
  const outputQty = parseRecetaOutputQuantity(receta);
  if (!receta || !Array.isArray(receta.items) || receta.items.length === 0) return [];
  return receta.items.map((it) => {
    const product = it.product?.name || it.product_label || (it.product_id ? `Producto #${it.product_id}` : 'Insumo');
    // Siempre priorizar la unidad maestra configurada en el producto.
    const unit = it.product?.unit_of_measure || it.unit_of_measure || 'kg';
    const qBaseNet = parseFloat(String(it.quantity ?? 0).replace(',', '.')) || 0;
    const mermaPct = parseRecetaMermaPct(it);
    const qNet = Math.round(qBaseNet * f * RECETA_QTY_DECIMAL_FACTOR) / RECETA_QTY_DECIMAL_FACTOR;
    const q = cantidadBrutaConMerma(qNet, mermaPct);
    const qMerma = Math.round((q - qNet) * RECETA_QTY_DECIMAL_FACTOR) / RECETA_QTY_DECIMAL_FACTOR;
    const qUnitNet = Math.round((qBaseNet / outputQty) * RECETA_QTY_DECIMAL_FACTOR) / RECETA_QTY_DECIMAL_FACTOR;
    const qUnit = cantidadBrutaConMerma(qUnitNet, mermaPct);
    const pu = precioUnitarioInsumoDesdeItem(it);
    const sub = Math.round(q * pu * 100) / 100;
    const byproductPct = parseFloat(String(it.byproduct_pct ?? 0).replace(',', '.'));
    const byproductProductId = it.byproduct_product_id ?? it.byproduct_product?.id ?? null;
    const safeByproductPct = Number.isFinite(byproductPct) && byproductPct > 0 ? byproductPct : 0;
    const byproductQty =
      byproductProductId != null && safeByproductPct > 0 && q > 0
        ? Math.round(q * (safeByproductPct / 100) * RECETA_QTY_DECIMAL_FACTOR) / RECETA_QTY_DECIMAL_FACTOR
        : 0;
    return {
      product,
      product_id: it.product_id ?? it.product?.id ?? null,
      unit,
      quantity: q,
      quantity_net: qNet,
      quantity_merma: qMerma,
      merma_pct: mermaPct,
      quantityUnitario: qUnit,
      quantityUnitarioNet: qUnitNet,
      precioUnitario: pu,
      subtotal: sub,
      byproduct_product_id: byproductProductId != null ? Number(byproductProductId) : null,
      byproduct_pct: safeByproductPct,
      byproduct_quantity: byproductQty,
      byproduct_name:
        it.byproduct_product?.name ||
        it.byproduct_product_label ||
        (byproductProductId ? `Producto #${byproductProductId}` : null),
      byproduct_unit:
        it.byproduct_product?.unit_of_measure ||
        it.byproduct_unit_of_measure ||
        unit,
    };
  });
}

/**
 * Cantidad de insumo en recetas: siempre 4 decimales (criterio alineado con la ficha de receta).
 */
export function formatearCantidadReceta4Dec(value) {
  const n = typeof value === 'number' ? value : parseFloat(String(value ?? '').replace(/\s/g, '').replace(',', '.'));
  const x = Number.isFinite(n) ? n : 0;
  return x.toLocaleString('es-PE', { minimumFractionDigits: 4, maximumFractionDigits: 4, useGrouping: false });
}

/**
 * Costo estimado de una celda: suma (costo base receta API × nº platos) por cada línea del menú.
 * @param {Map<string, object>|null} [recetasFullById] Recetas con `items`; si falta, costo 0 salvo que `light` traiga items.
 */
export function costoTotalCeldaPlatos(cantidadStr, textoCelda, catalogoRecetas, recetasFullById) {
  const partes = parseLineasCelda(textoCelda);
  const n = Math.max(0, parseInt(String(cantidadStr || '').replace(/\D/g, ''), 10) || 0);
  if (n === 0 || partes.length === 0) return 0;
  const m = recetasFullById instanceof Map ? recetasFullById : null;
  let sum = 0;
  for (const nombrePlato of partes) {
    const light = buscarRecetaPlatoEnCatalogo(nombrePlato, catalogoRecetas);
    if (!light?.id) continue;
    const full = m?.get(String(light.id)) || light;
    const outputQty = parseRecetaOutputQuantity(full);
    sum += costoBaseRecetaPlatoApi(full) * (n / outputQty);
  }
  return Math.round(sum * 100) / 100;
}

/** Resumen de insumos (API) para el modal de salida. */
export function buildResumenInsumosSalidaApi(slots, cantidades, catalogoRecetas, recetasFullById) {
  const map = new Map();
  if (!Array.isArray(slots)) return { filas: [], totalCosto: 0 };
  const m = recetasFullById instanceof Map ? recetasFullById : new Map();
  for (const slot of slots) {
    const n = Math.max(0, parseInt(String(cantidades?.[slot.sk] || '').replace(/\D/g, ''), 10) || 0);
    if (n <= 0) continue;
    for (const nombrePlato of parseLineasCelda(slot.texto)) {
      const light = buscarRecetaPlatoEnCatalogo(nombrePlato, catalogoRecetas);
      if (!light?.id) continue;
      const full = m.get(String(light.id)) || light;
      const ings = lineasInsumoEscaladosRecetaApi(full, n);
      for (const row of ings) {
        const key = row.product_id != null ? `id:${row.product_id}|||${row.unit}` : `${row.product}|||${row.unit}`;
        const prev = map.get(key) || {
          product: row.product,
          product_id: row.product_id ?? null,
          unit: row.unit,
          quantity: 0,
          quantity_net: 0,
          quantity_merma: 0,
          subtotal: 0,
        };
        prev.quantity = Math.round((prev.quantity + row.quantity) * RECETA_QTY_DECIMAL_FACTOR) / RECETA_QTY_DECIMAL_FACTOR;
        prev.quantity_net = Math.round(
          (Number(prev.quantity_net || 0) + Number((row.quantity_net ?? row.quantity) || 0)) * RECETA_QTY_DECIMAL_FACTOR,
        ) / RECETA_QTY_DECIMAL_FACTOR;
        prev.quantity_merma = Math.round(
          (Number(prev.quantity_merma || 0) + Number(row.quantity_merma || 0)) * RECETA_QTY_DECIMAL_FACTOR,
        ) / RECETA_QTY_DECIMAL_FACTOR;
        prev.subtotal = Math.round((prev.subtotal + row.subtotal) * 100) / 100;
        map.set(key, prev);
      }
    }
  }
  const filas = Array.from(map.values()).sort((a, b) => a.product.localeCompare(b.product, 'es'));
  const totalCosto = Math.round(filas.reduce((s, r) => s + (Number(r.subtotal) || 0), 0) * 100) / 100;
  return { filas, totalCosto: Number.isFinite(totalCosto) ? totalCosto : 0 };
}

export function slotKey(diaKey, rowKey) {
  return `${diaKey}::${rowKey}`;
}

/**
 * Agrega todos los insumos de las celdas con cantidad > 0 (recetas escaladas por nº de platos).
 * Devuelve filas por producto+unidad y costo total estimado.
 */
export function buildResumenInsumosSalida(slots, cantidades) {
  const map = new Map();
  if (!Array.isArray(slots)) return { filas: [], totalCosto: 0 };
  for (const slot of slots) {
    const n = Math.max(0, parseInt(String(cantidades?.[slot.sk] || '').replace(/\D/g, ''), 10) || 0);
    if (n <= 0) continue;
    const partes = parseLineasCelda(slot.texto);
    for (const nombrePlato of partes) {
      const code = resolverCodigoRecetaPorNombrePlato(nombrePlato);
      if (!code) continue;
      const ings = ingredientesEscalados(code, n);
      for (const row of ings) {
        const key = `${row.product}|||${row.unit}`;
        const prev = map.get(key) || { product: row.product, unit: row.unit, quantity: 0, subtotal: 0 };
        prev.quantity = Math.round((prev.quantity + row.quantity) * RECETA_QTY_DECIMAL_FACTOR) / RECETA_QTY_DECIMAL_FACTOR;
        prev.subtotal = Math.round((prev.subtotal + row.subtotal) * 100) / 100;
        map.set(key, prev);
      }
    }
  }
  const filas = Array.from(map.values()).sort((a, b) => a.product.localeCompare(b.product, 'es'));
  const totalCosto = Math.round(filas.reduce((s, r) => s + r.subtotal, 0) * 100) / 100;
  return { filas, totalCosto };
}

/** Resumen por fila del menú con cantidad de platos > 0. */
export function buildResumenPlatosPorCelda(slots, cantidades) {
  if (!Array.isArray(slots)) return [];
  const out = [];
  for (const slot of slots) {
    const n = Math.max(0, parseInt(String(cantidades?.[slot.sk] || '').replace(/\D/g, ''), 10) || 0);
    if (n <= 0) continue;
    const partes = parseLineasCelda(slot.texto);
    if (partes.length === 0) continue;
    out.push({
      tiempo: slot.tiempoLabel,
      tipo: slot.tipoLabel,
      menu: slot.texto,
      cantidadPlatos: n,
    });
  }
  return out;
}

function normalizeSalidaDia(raw) {
  const id = raw.id || `salida-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
  const cicloId = String(raw.cicloId || '');
  const diaKey = String(raw.diaKey || '').toLowerCase();
  const estado =
    raw.estado === ESTADO_SALIDA.SALIDA_PRODUCCION
      ? ESTADO_SALIDA.SALIDA_PRODUCCION
      : ESTADO_SALIDA.PRESUPUESTADO;
  const cantidades =
    raw.cantidades && typeof raw.cantidades === 'object' ? { ...raw.cantidades } : {};
  const updatedAt = raw.updatedAt ? String(raw.updatedAt) : new Date().toISOString();
  /** Tras Guardar: cantidades ya no editables hasta "Editar cantidades". */
  const bloquearCantidades = raw.bloquearCantidades === true;
  /** Hora de salida a producción (HH:mm) al confirmar salida. */
  let horaSalidaProduccion = '';
  if (raw.horaSalidaProduccion != null && String(raw.horaSalidaProduccion).trim() !== '') {
    const h = String(raw.horaSalidaProduccion).trim().slice(0, 5);
    horaSalidaProduccion = /^\d{1,2}:\d{2}$/.test(h) ? h : '';
  }
  const salidaGuideId = raw.salidaGuideId != null && raw.salidaGuideId !== '' ? Number(raw.salidaGuideId) : null;
  const entradaGuideId = raw.entradaGuideId != null && raw.entradaGuideId !== '' ? Number(raw.entradaGuideId) : null;
  return {
    id,
    cicloId,
    diaKey,
    estado,
    cantidades,
    updatedAt,
    bloquearCantidades,
    horaSalidaProduccion,
    salidaGuideId: Number.isFinite(salidaGuideId) ? salidaGuideId : null,
    entradaGuideId: Number.isFinite(entradaGuideId) ? entradaGuideId : null,
  };
}

/** Migra registros v1 (solo cicloId) a v2 (cicloId + diaKey) agrupando claves `dia::row`. */
function migrateV1ListToV2(listV1) {
  const out = [];
  if (!Array.isArray(listV1)) return out;
  for (const old of listV1) {
    if (old.diaKey && esDiaKeyValido(old.diaKey)) {
      out.push(normalizeSalidaDia(old));
      continue;
    }
    const cid = String(old.cicloId || '');
    if (!cid) continue;
    const cant = old.cantidades && typeof old.cantidades === 'object' ? old.cantidades : {};
    const keys = Object.keys(cant);
    if (keys.length === 0) continue;
    const byDay = {};
    for (const k of keys) {
      const idx = k.indexOf('::');
      if (idx <= 0) continue;
      const diaKey = k.slice(0, idx);
      if (!esDiaKeyValido(diaKey)) continue;
      if (!byDay[diaKey]) byDay[diaKey] = {};
      byDay[diaKey][k] = cant[k];
    }
    const dias = Object.keys(byDay);
    if (dias.length === 0) continue;
    for (const diaKey of dias) {
      out.push(
        normalizeSalidaDia({
          cicloId: cid,
          diaKey,
          estado: old.estado,
          cantidades: byDay[diaKey],
          updatedAt: old.updatedAt,
        }),
      );
    }
  }
  return out;
}

export function loadSalidasProduccion() {
  try {
    let raw = localStorage.getItem(STORAGE_SALIDAS_PRODUCCION);
    if (!raw) {
      const leg = localStorage.getItem(STORAGE_LEGACY_V1);
      if (leg) {
        try {
          const p = JSON.parse(leg);
          const migrated = migrateV1ListToV2(Array.isArray(p) ? p : []);
          localStorage.setItem(STORAGE_SALIDAS_PRODUCCION, JSON.stringify(migrated));
          return migrated;
        } catch {
          /* ignore */
        }
      }
      return [];
    }
    const p = JSON.parse(raw);
    if (!Array.isArray(p)) return [];
    return p
      .map(normalizeSalidaDia)
      .filter((s) => s.cicloId && esDiaKeyValido(s.diaKey));
  } catch {
    return [];
  }
}

export function saveSalidasProduccion(list) {
  localStorage.setItem(STORAGE_SALIDAS_PRODUCCION, JSON.stringify(list));
}

export function getSalidaPorCicloYDia(cicloId, diaKey, list) {
  const cid = String(cicloId || '');
  const d = String(diaKey || '').toLowerCase();
  return (list || []).find((s) => s.cicloId === cid && s.diaKey === d) || null;
}

export function upsertSalidaDia(partial) {
  const list = loadSalidasProduccion();
  const cid = String(partial.cicloId || '');
  const dk = String(partial.diaKey || '').toLowerCase();
  if (!cid || !esDiaKeyValido(dk)) return null;
  const idx = list.findIndex((s) => s.cicloId === cid && s.diaKey === dk);
  const prev = idx >= 0 ? list[idx] : null;
  const merged = {
    ...prev,
    ...partial,
    cicloId: cid,
    diaKey: dk,
    id: prev?.id,
  };
  if (partial.bloquearCantidades === undefined && prev && prev.bloquearCantidades != null) {
    merged.bloquearCantidades = prev.bloquearCantidades;
  }
  if (partial.horaSalidaProduccion === undefined && prev && prev.horaSalidaProduccion != null) {
    merged.horaSalidaProduccion = prev.horaSalidaProduccion;
  }
  if (partial.salidaGuideId === undefined && prev && prev.salidaGuideId != null) {
    merged.salidaGuideId = prev.salidaGuideId;
  }
  if (partial.entradaGuideId === undefined && prev && prev.entradaGuideId != null) {
    merged.entradaGuideId = prev.entradaGuideId;
  }
  const n = normalizeSalidaDia(merged);
  if (idx >= 0) {
    list[idx] = { ...n, id: list[idx].id };
  } else {
    list.push(n);
  }
  saveSalidasProduccion(list);
  return list.find((s) => s.cicloId === n.cicloId && s.diaKey === n.diaKey);
}

/** Elimina todas las salidas a producción guardadas para un cíclico (p. ej. al borrar el cíclico). */
export function eliminarSalidasPorCicloId(cicloId) {
  const cid = String(cicloId || '');
  if (!cid) return;
  const list = loadSalidasProduccion().filter((s) => s.cicloId !== cid);
  saveSalidasProduccion(list);
}

/**
 * Filas para el listado: un renglón por cada día activo de cada cíclico.
 * @param {unknown} [plantillasList] Si es un array, plantillas desde API; si no, se usa localStorage.
 * @param {unknown} [ciclosList] Si es un array, cíclicos (p. ej. desde API); si no, se usa `loadCiclosCiclicos()`.
 */
export function listarFilasSalidasDelDia(plantillasList, ciclosList) {
  const ciclos = Array.isArray(ciclosList) ? ciclosList : loadCiclosCiclicos();
  const plantillas = Array.isArray(plantillasList) ? plantillasList : loadPlantillasCiclicos();
  const salidas = loadSalidasProduccion();
  const rows = [];
  for (const ciclo of ciclos) {
    const p = plantillas.find((pl) => String(pl.id) === String(ciclo.plantillaId));
    if (!p) continue;
    const fechas = fechasPorDiaDesdeInicio(ciclo.fechaInicio);
    for (const diaKey of DIAS_KEYS_ORDEN) {
      if (!p.diasSemana?.[diaKey]) continue;
      const fechaYmd = fechas[diaKey] || '';
      const s = getSalidaPorCicloYDia(ciclo.id, diaKey, salidas);
      rows.push({
        cicloId: ciclo.id,
        cicloNombre: ciclo.nombre,
        diaKey,
        diaLabel: DIAS_LABEL[diaKey] || diaKey,
        fechaYmd,
        semanaLabel: etiquetaSemana(ciclo.anio, ciclo.numeroSemana),
        estado: s?.estado ?? null,
        updatedAt: s?.updatedAt,
      });
    }
  }
  rows.sort((a, b) => {
    if (a.fechaYmd !== b.fechaYmd) return String(b.fechaYmd).localeCompare(String(a.fechaYmd));
    return String(a.cicloNombre).localeCompare(String(b.cicloNombre), 'es');
  });
  return rows;
}
